<template>
  <div
    ref="toast"
    class="fixed inset-0 flex items-end justify-center p-6 pointer-events-none z-999 transition-all sm:justify-end"
  >
    <transition
      enter-active-class="transition-all ease-in duration-200"
      enter-class="opacity-0 translate-y-full lg:-translate-y-full"
      enter-to-class="opacity-100"
      leave-active-class="transition-all ease-out duration-200"
      leave-class="translate-y-0 opacity-100"
      leave-to-class="translate-y-full lg:-translate-y-full opacity-0"
    >
      <div
        v-if="visible"
        class="max-w-sm w-full bg-white shadow-lg rounded pointer-events-auto overflow-hidden py-4 px-5 leading-4 text-sm"
        :class="toastClasses"
      >
        <p
          v-if="title"
          class="flex justify-between font-bold"
          :class="titleClasses"
        >
          <span>{{ title }}</span>
          <button
            :aria-label="$t('common.dismiss')"
            class="inline-flex text-neutral-400 focus:outline-none focus:text-neutral-500 transition"
            type="button"
            @click="hide"
          >
            <fa
              :icon="['fal', 'times']"
              class="text-base"
            />
          </button>
        </p>
        <p
          class="mt-1 text-neutral-500"
          v-html="message"
        />
      </div>
    </transition>
  </div>
</template>

<script>
import { library } from '@fortawesome/fontawesome-svg-core';
import { faTimes } from '@fortawesome/pro-light-svg-icons';
import assign from 'lodash/assign';
import clone from 'lodash/clone';
import Vue from 'vue';

import setGlobalProperty from '@/helpers/setGlobalProperty';

library.add(faTimes);

const Level = {
  Success: 'success',
  Error: 'error',
};

export default {
  name: 'VetsterToast',

  data() {
    const defaultOptions = {
      status: Level.Success,
      timeout: 6000,
    };

    return {
      defaultOptions,
      intervalId: null,
      message: '',
      options: clone(defaultOptions),
      timeoutId: null,
      visible: false,
    };
  },

  computed: {
    isError() {
      return this.options.status === Level.Error;
    },
    isSuccess() {
      return this.options.status === Level.Success;
    },
    title() {
      if (this.isError) {
        return this.$t('toast.error');
      }

      if (this.isSuccess) {
        return this.$t('toast.success');
      }

      return '';
    },
    titleClasses() {
      return {
        'text-red-700': this.isError,
        'text-green-700': this.isSuccess,
      };
    },
    toastClasses() {
      return {
        'border-l-[6px]': this.isError || this.isSuccess,
        'border-red-700': this.isError,
        'border-green-700': this.isSuccess,
      };
    },
  },

  created() {
    setGlobalProperty(Vue, '$toast', this.show);
  },

  methods: {
    getAnchorElement() {
      return this.getBottomElements().filter(Boolean).map((el) => {
        const { parentElement, firstChild } = el;

        return [
          el,
          parentElement,
          firstChild,
        ].find((anchor) => anchor?.hasAttribute?.('data-toast-anchor'));
      }).filter(Boolean)[0];
    },
    getBottomElements() {
      return [
        document.elementFromPoint(window.innerWidth - 1, window.innerHeight - 1), // bottom-right
        document.elementFromPoint(window.innerWidth / 2, window.innerHeight - 1), // bottom-center
        document.elementFromPoint(1, window.innerHeight - 1), // bottom-left
      ];
    },
    hide() {
      this.visible = false;
      clearInterval(this.intervalId);
    },
    reset() {
      this.options = clone(this.defaultOptions);
    },
    show(message, options) {
      this.reset();
      if (options) {
        assign(this.options, options);
      }

      this.message = message;
      this.visible = true;

      clearInterval(this.intervalId);
      clearTimeout(this.timeoutId);

      if (this.options.timeout > 0) {
        this.timeoutId = setTimeout(this.hide, this.options.timeout);
      }

      this.updatePosition();

      this.intervalId = setInterval(this.updatePosition, 100);
    },
    updatePosition() {
      const { toast } = this.$refs;
      const el = this.getAnchorElement();

      toast.style.marginBottom = el ? `${el.offsetHeight}px` : '';
      toast.style.paddingBottom = el ? '10px' : '';
    },
  },
};
</script>
